import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Filter, Users, Mail, Phone, MapPin, Calendar, BookOpen,
  Award, AlertTriangle, TrendingUp, TrendingDown, Eye, Edit,
  Download, Plus, Building2, Clock, CheckCircle, XCircle, Bell,
  Loader2, MessageCircle, Hash, GraduationCap, Timer, Trash2,
} from 'lucide-react';
import apiClient from '../../services/api';
import ModalCadastroAluno from './ModalCadastroAluno';
import { glass, text } from './ui/theme';
import GlassBackdrop from './ui/GlassBackdrop';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Aluno {
  id:             string;
  systemId:       string;
  nome:           string;
  email:          string;
  photoUrl:       string;
  cpf:            string;
  cin:            string;
  telefone:       string;
  whatsapp:       string;
  dataNascimento: string;
  dataCadastro:   string;
  endereco:       string;
  cidade:         string;
  estado:         string;
  cep:            string;
  nomeCurso:          string;
  cargaHoraria:       number;
  dataInicioCurso:    string;
  dataTerminoCurso:   string;
  Afiliado:           string;
  AfiliadoId:         string;
  cursosAtivos:       number;
  cursosCompletos:    number;
  certificados:       number;
  statusMatricula:    'ativo' | 'inativo' | 'trancado' | 'concluido' | 'formado';
  risco:              'baixo' | 'medio' | 'alto';
  ultimaAtividade:    string;
  diasInativo:        number;
}

interface Stats {
  totalAlunos: number; ativos: number; inativos: number;
  formados: number;
  altaRisco: number; mediaRisco: number; baixoRisco: number;
  ultimosSeteDias: number; totalCertificados: number;
}

interface AfiliadoOption {
  id: string;
  nome: string;
  quantidadeAlunos: number;
}

// ─── Mappers ─────────────────────────────────────────────────────────────────
const mapStatus = (s: string): Aluno['statusMatricula'] => {
  const m: Record<string, Aluno['statusMatricula']> = {
    active:    'ativo',    inactive:  'inativo',
    suspended: 'trancado', completed: 'concluido',
    graduated: 'formado',  // ← ADD
    ativo:     'ativo',    inativo:   'inativo',
    trancado:  'trancado', concluido: 'concluido',
    formado:   'formado',  // ← ADD
  };
  return m[s] ?? 'inativo';
};

const mapRisco = (r: string): Aluno['risco'] => {
  const m: Record<string, Aluno['risco']> = {
    low: 'baixo', medium: 'medio', high: 'alto',
    baixo: 'baixo', medio: 'medio', alto: 'alto',
  };
  return m[r] ?? 'baixo';
};

const mapAluno = (s: any): Aluno => ({
  id:        s.id,
  systemId:  s.systemId ?? s.id,
  nome:      s.fullName ?? s.nome ?? '',
  email:     s.email ?? s.user?.email ?? '',
  photoUrl:  s.photoUrl ?? s.user?.photoUrl ?? '',
  cpf:       s.cpf ?? '',
  cin:       s.cin ?? '',
  telefone:  s.phone    ?? s.telefone  ?? '',
  whatsapp:  s.whatsapp ?? '',
  dataNascimento: s.birthdate      ?? s.birthDate      ?? s.dataNascimento ?? '',
  dataCadastro:   s.enrollmentDate ?? s.createdAt      ?? s.dataCadastro   ?? '',
  endereco: s.address    ?? s.endereco  ?? '',
  cidade:   s.city       ?? s.cidade    ?? '',
  estado:   s.state      ?? s.estado    ?? '',
  cep:      s.postalCode ?? s.cep       ?? '',
  nomeCurso:        s.courseName      ?? s.nomeCurso        ?? '',
  cargaHoraria:     s.courseWorkload  ?? s.cargaHoraria     ?? 0,
  dataInicioCurso:  s.courseStartDate ?? s.dataInicioCurso  ?? '',
  dataTerminoCurso: s.courseEndDate   ?? s.dataTerminoCurso ?? '',
  Afiliado:         s.affiliate?.schoolName ?? s.affiliateName ?? s.Afiliado ?? '',
  AfiliadoId:       s.affiliateId  ?? s.AfiliadoId ?? '',
  cursosAtivos:     s.activeCourses    ?? s.cursosAtivos    ?? 0,
  cursosCompletos:  s.completedCourses ?? s.cursosCompletos ?? 0,
  certificados:     s.certificatesCount ?? s.certificados  ?? 0,
  statusMatricula:  mapStatus(s.status ?? s.statusMatricula ?? 'inactive'),
  risco:            mapRisco(s.evasionRisk ?? s.risco ?? 'low'),
  ultimaAtividade:  s.lastActivity ?? s.ultimaAtividade ?? 'Sem registro',
  diasInativo:      s.inactiveDays  ?? s.diasInativo    ?? 0,
});

const fmtDate = (d: string) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR'); }
  catch { return d; }
};

// ─── AlunoAvatar ─────────────────────────────────────────────────────────────
const AlunoAvatar: React.FC<{ aluno: Aluno; size?: 'sm' | 'md' | 'lg' }> = ({ aluno, size = 'md' }) => {
  const [imgError, setImgError] = useState(false);
  const sz = { sm: 'w-10 h-10 text-base', md: 'w-16 h-16 text-2xl', lg: 'w-24 h-24 text-4xl' }[size];

  return aluno.photoUrl && !imgError ? (
    <img key={aluno.photoUrl} src={aluno.photoUrl} alt={aluno.nome}
      className={`${sz} rounded-full object-cover border-2 border-green-200 flex-shrink-0`}
      onError={() => setImgError(true)} />
  ) : (
    <div className={`${sz} rounded-full bg-gradient-to-br from-green-500 to-emerald-600
      flex items-center justify-center text-white font-bold flex-shrink-0`}>
      {aluno.nome.charAt(0).toUpperCase()}
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const GerenciamentoAlunos: React.FC = () => {
  const [alunos, setAlunos]                     = useState<Aluno[]>([]);
  const [stats, setStats]                       = useState<Stats | null>(null);
  const [afiliados, setAfiliados]               = useState<AfiliadoOption[]>([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState<string | null>(null);
  const [busca, setBusca]                       = useState('');
  const [buscaInput, setBuscaInput]             = useState('');
  const [filtroStatus, setFiltroStatus]         = useState<string>('todos');
  const [filtroRisco, setFiltroRisco]           = useState<string>('todos');
  const [filtroAfiliado, setFiltroAfiliado]     = useState<string>('todos');
  const [alunoSelecionado, setAlunoSelecionado] = useState<Aluno | null>(null);
  const [modalAberto, setModalAberto]           = useState(false);
  const [modalCadastro, setModalCadastro]       = useState(false);
  const [paginaAtual, setPaginaAtual]           = useState(1);
  const [totalRegistros, setTotalRegistros]     = useState(0);
  const [salvando, setSalvando]                 = useState(false);
  const itensPorPagina = 20;

  useEffect(() => {
    const t = setTimeout(() => { setBusca(buscaInput); setPaginaAtual(1); }, 400);
    return () => clearTimeout(t);
  }, [buscaInput]);

  const fetchAlunos = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let data: any;
      if (busca.trim().length >= 2) {
        const params = new URLSearchParams({
          query: busca,
          page: String(paginaAtual),
          limit: String(itensPorPagina),
        });
        if (filtroStatus !== 'todos')   params.set('status', filtroStatus);
        if (filtroAfiliado !== 'todos') params.set('affiliateId', filtroAfiliado);
        data = await apiClient.get(`/students/search?${params}`);
      } else {
        const params = new URLSearchParams({ page: String(paginaAtual), limit: String(itensPorPagina) });
        if (filtroStatus !== 'todos')   params.set('status',      filtroStatus);
        if (filtroAfiliado !== 'todos') params.set('affiliateId', filtroAfiliado);
        data = await apiClient.get(`/students?${params}`);
      }
      setAlunos((data.students ?? data.results ?? []).map(mapAluno));
      setTotalRegistros(data.total ?? 0);
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar alunos');
    } finally { setLoading(false); }
  }, [busca, paginaAtual, filtroStatus, filtroAfiliado]);

  const fetchStats = useCallback(async () => {
    try {
      const data: any = await apiClient.get('/students/stats/summary');
      setStats({
        totalAlunos:       data.total             ?? data.totalStudents     ?? 0,
        ativos:            data.active            ?? data.ativos            ?? 0,
        inativos:          data.inactive          ?? data.inativos          ?? 0,
        formados:          data.graduated         ?? data.formados          ?? 0,
        altaRisco:         data.highRisk          ?? data.altaRisco         ?? 0,
        mediaRisco:        data.mediumRisk        ?? data.mediaRisco        ?? 0,
        baixoRisco:        data.lowRisk           ?? data.baixoRisco        ?? 0,
        ultimosSeteDias:   data.newLast7Days      ?? data.ultimosSeteDias   ?? 0,
        totalCertificados: data.totalCertificates ?? data.totalCertificados ?? 0,
      });
    } catch { /* stats opcionais */ }
  }, []);

  const fetchAfiliados = useCallback(async () => {
    try {
      const data: any = await apiClient.get('/students/filter-options');
      setAfiliados((data.affiliates ?? []).map((affiliate: any) => ({
        id: affiliate.id,
        nome: affiliate.name,
        quantidadeAlunos: Number(affiliate.studentCount) || 0,
      })));
    } catch {
      setAfiliados([]);
    }
  }, []);

  useEffect(() => { fetchAlunos(); }, [fetchAlunos]);
  useEffect(() => { fetchStats();  }, [fetchStats]);
  useEffect(() => { fetchAfiliados(); }, [fetchAfiliados]);

  const alunosFiltrados = useMemo(() => {
    if (filtroRisco === 'todos') return alunos;
    return alunos.filter(a => a.risco === filtroRisco);
  }, [alunos, filtroRisco]);

  const totalPaginas = Math.ceil(totalRegistros / itensPorPagina);

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleAcionarRisco = async (aluno: Aluno) => {
    setSalvando(true);
    try {
      await apiClient.post('/notifications', {
        userId:  aluno.id,
        title:   'Aluno em Risco de Evasão',
        message: `O aluno ${aluno.nome} está há ${aluno.diasInativo} dias sem atividade. Entre em contato.`,
        type:    'evasion_alert',
      });
      alert(`✅ Notificação enviada para ${aluno.nome}`);
    } catch { alert('Erro ao enviar notificação'); }
    finally { setSalvando(false); }
  };

  const handleAtualizarStatus = async (id: string, novoStatus: string) => {
    setSalvando(true);
    try {
      await apiClient.patch(`/students/${id}`, { status: novoStatus });
      fetchAlunos(); setModalAberto(false);
    } catch { alert('Erro ao atualizar status'); }
    finally { setSalvando(false); }
  };

  const handleDeletar = async (id: string, nome: string) => {
    if (!window.confirm(`Excluir permanentemente o aluno "${nome}"?\n\nEsta ação não pode ser desfeita.`)) return;
    setSalvando(true);
    try {
      await apiClient.delete(`/students/${id}`);
      fetchAlunos(); fetchStats();
      if (modalAberto) setModalAberto(false);
    } catch { alert('Erro ao excluir aluno'); }
    finally { setSalvando(false); }
  };

  const handleExportar = async () => {
    try {
      const data: any = await apiClient.get('/students?limit=9999');
      const rows = (data.students ?? []).map(mapAluno);
      const csv = [
        'ID,Nome,Email,CPF,CIN,Telefone,WhatsApp,Nascimento,Curso,Carga,Início,Término,Afiliado,Status,Risco,Cidade,Estado',
        ...rows.map((a: Aluno) =>
          `${a.systemId},"${a.nome}",${a.email},${a.cpf},${a.cin},${a.telefone},${a.whatsapp},${a.dataNascimento},"${a.nomeCurso}",${a.cargaHoraria},${a.dataInicioCurso},${a.dataTerminoCurso},"${a.Afiliado}",${a.statusMatricula},${a.risco},${a.cidade},${a.estado}`
        ),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `alunos_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch { alert('Erro ao exportar'); }
  };

  // ─── Badges ───────────────────────────────────────────────────────────────
const getStatusBadge = (status: Aluno['statusMatricula']) => {
  const badges = {
    ativo:     { bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-300',  icon: CheckCircle,   label: 'Ativo'     },
    inativo:   { bg: 'bg-gray-100',   text: 'text-gray-800',   border: 'border-gray-300',   icon: XCircle,       label: 'Inativo'   },
    trancado:  { bg: 'bg-amber-100',  text: 'text-amber-800',  border: 'border-amber-300',  icon: Clock,         label: 'Trancado'  },
    concluido: { bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300',   icon: Award,         label: 'Concluído' },
    formado:   { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', icon: GraduationCap, label: 'Formado'   }, // ← ADD
  };
  const b = badges[status] ?? badges.inativo;
  const Icon = b.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${b.bg} ${b.text} ${b.border}`}>
      <Icon className="w-3.5 h-3.5" />{b.label}
    </span>
  );
};
  const getRiscoBadge = (risco: Aluno['risco']) => {
    const badges = {
      baixo: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: TrendingUp,    label: 'Baixo' },
      medio: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', icon: AlertTriangle, label: 'Médio' },
      alto:  { bg: 'bg-red-100',   text: 'text-red-800',   border: 'border-red-300',   icon: TrendingDown,  label: 'Alto'  },
    };
    const b = badges[risco]; const Icon = b.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${b.bg} ${b.text} ${b.border}`}>
        <Icon className="w-3.5 h-3.5" />{b.label}
      </span>
    );
  };

  const estatisticas = stats ?? {
    totalAlunos:       totalRegistros,
    ativos:            alunos.filter(a => a.statusMatricula === 'ativo').length,
    inativos:          alunos.filter(a => a.statusMatricula === 'inativo').length,
    formados:          alunos.filter(a => a.statusMatricula === 'formado').length,
    altaRisco:         alunos.filter(a => a.risco === 'alto').length,
    mediaRisco:        alunos.filter(a => a.risco === 'medio').length,
    baixoRisco:        alunos.filter(a => a.risco === 'baixo').length,
    ultimosSeteDias:   0,
    totalCertificados: alunos.reduce((s, a) => s + a.certificados, 0),
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 relative">
      <GlassBackdrop />

      {/* HEADER */}
      <div className={`${glass.card} p-6`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-cosmos-900 tracking-tight mb-1">Gerenciamento de Alunos</h2>
            <p className="text-sm text-cosmos-500/80">Monitore alunos, identifique riscos de evasão e tome ações</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleExportar}
              className={`flex items-center gap-2 px-3 py-2 ${glass.pill} text-cosmos-600 hover:text-cosmos-900 transition-colors text-sm font-medium`}>
              <Download className="w-4 h-4" /> Exportar
            </button>
            <button onClick={() => setModalCadastro(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-cosmos-900 text-white text-sm font-medium hover:bg-cosmos-800 transition-all">
              <Plus className="w-4 h-4" /> Novo Aluno
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {[
            { label: 'Total',        value: estatisticas.totalAlunos },
            { label: 'Ativos',       value: estatisticas.ativos },
            { label: 'Inativos',     value: estatisticas.inativos },
            { label: 'Novos (7d)',   value: estatisticas.ultimosSeteDias },
            { label: 'Formados',     value: estatisticas.formados },
          ].map(s => (
            <div key={s.label} className="relative bg-cosmos-50/60 rounded-2xl p-4 border border-cosmos-100/70">
              <p className={text.label}>{s.label}</p>
              <p className="text-2xl font-semibold text-cosmos-900 mt-1">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" value={buscaInput} onChange={e => setBuscaInput(e.target.value)}
              placeholder="Buscar por nome, email, CPF..."
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2
                focus:ring-green-500 focus:border-transparent transition-all text-gray-900 font-semibold" />
          </div>
          <div className="flex items-center gap-3 bg-gray-50 border-2 border-gray-300 rounded-xl px-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <select value={filtroStatus} onChange={e => { setFiltroStatus(e.target.value); setPaginaAtual(1); }}
              className="bg-transparent outline-none py-3 text-gray-900 font-bold w-full">
              <option value="todos">Todos Status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
              <option value="suspended">Trancados</option>
              <option value="graduated">Formados</option> 
            </select>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 border-2 border-gray-300 rounded-xl px-4">
            <AlertTriangle className="w-5 h-5 text-gray-600" />
            <select value={filtroRisco} onChange={e => setFiltroRisco(e.target.value)}
              className="bg-transparent outline-none py-3 text-gray-900 font-bold w-full">
              <option value="todos">Todos Riscos</option>
              <option value="alto">Alto Risco</option>
              <option value="medio">Médio Risco</option>
              <option value="baixo">Baixo Risco</option>
            </select>
          </div>
          <div className="flex items-center gap-3 bg-gray-50 border-2 border-gray-300 rounded-xl px-4">
            <Building2 className="w-5 h-5 text-gray-600" />
            <select value={filtroAfiliado} onChange={e => { setFiltroAfiliado(e.target.value); setPaginaAtual(1); }}
              className="bg-transparent outline-none py-3 text-gray-900 font-bold w-full">
              <option value="todos">Todos Afiliados</option>
              {afiliados.map(a => (
                <option key={a.id} value={a.id}>
                  {a.nome} ({a.quantidadeAlunos})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* TABELA */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-20 flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
            <p className="text-gray-500 font-semibold">Carregando alunos...</p>
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-600 font-bold">{error}</p>
            <button onClick={fetchAlunos}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700">
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {['Aluno', 'Afiliado', 'Curso', 'Status', 'Risco', 'Atividade', ''].map(h => (
                      <th key={h} className={`px-6 py-4 text-sm font-black text-gray-900 ${h === '' ? 'text-right' : 'text-left'}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {alunosFiltrados.map(aluno => (
                    <tr key={aluno.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <AlunoAvatar aluno={aluno} size="sm" />
                          <div>
                            <p className="font-bold text-gray-900">{aluno.nome}</p>
                            <p className="text-sm text-gray-500">{aluno.systemId}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-semibold text-gray-900">{aluno.Afiliado || '—'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-gray-900">{aluno.nomeCurso || '—'}</p>
                        {aluno.cargaHoraria > 0 && <p className="text-xs text-gray-500">{aluno.cargaHoraria}h</p>}
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(aluno.statusMatricula)}</td>
                      <td className="px-6 py-4">{getRiscoBadge(aluno.risco)}</td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900 font-semibold">
                          {aluno.diasInativo === 0 ? 'Hoje' : `${aluno.diasInativo}d atrás`}
                        </p>
                        <p className="text-xs text-gray-500">{aluno.ultimaAtividade}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {aluno.risco === 'alto' && (
                            <button onClick={() => handleAcionarRisco(aluno)} disabled={salvando}
                              className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors" title="Acionar risco">
                              <Bell className="w-5 h-5" />
                            </button>
                          )}
                          <button onClick={() => { setAlunoSelecionado(aluno); setModalAberto(true); }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Ver detalhes">
                            <Eye className="w-5 h-5" />
                          </button>
                          <button className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Editar">
                            <Edit className="w-5 h-5" />
                          </button>
                          <button onClick={() => handleDeletar(aluno.id, aluno.nome)} disabled={salvando}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50" title="Excluir">
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {alunosFiltrados.length === 0 && !loading && (
              <div className="py-12 text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-semibold">Nenhum aluno encontrado</p>
              </div>
            )}

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Mostrando <span className="font-bold text-gray-900">{alunosFiltrados.length}</span> de{' '}
                <span className="font-bold text-gray-900">{totalRegistros}</span> alunos
              </p>
              {totalPaginas > 1 && (
                <div className="flex gap-2">
                  <button onClick={() => setPaginaAtual(p => Math.max(1, p - 1))} disabled={paginaAtual === 1}
                    className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 disabled:opacity-50">
                    Anterior
                  </button>
                  <span className="px-3 py-1 text-gray-700 font-semibold">{paginaAtual} / {totalPaginas}</span>
                  <button onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))} disabled={paginaAtual === totalPaginas}
                    className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 disabled:opacity-50">
                    Próxima
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── MODAL DETALHES ──────────────────────────────────────────────────── */}
      {modalAberto && alunoSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto">

            <div className="p-6 bg-cosmos-700 rounded-t-2xl">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <AlunoAvatar aluno={alunoSelecionado} size="lg" />
                  <div>
                    <h4 className="text-xl font-semibold text-white tracking-tight">{alunoSelecionado.nome}</h4>
                    <p className="text-xs text-white/50 font-mono mt-0.5">{alunoSelecionado.systemId}</p>
                    <div className="flex flex-wrap gap-2 mt-2.5">
                      {getStatusBadge(alunoSelecionado.statusMatricula)}
                      {getRiscoBadge(alunoSelecionado.risco)}
                    </div>
                  </div>
                </div>
                <button onClick={() => setModalAberto(false)}
                  className="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-white/80 hover:text-white transition-colors">
                  <span className="text-lg leading-none">✕</span>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">

              {/* Dados Pessoais */}
              <div>
                <p className={`${text.label} mb-3`}>Dados Pessoais</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'E-mail',           value: alunoSelecionado.email,                   icon: Mail          },
                    { label: 'Telefone',          value: alunoSelecionado.telefone,                icon: Phone         },
                    { label: 'WhatsApp',          value: alunoSelecionado.whatsapp,                icon: MessageCircle },
                    { label: 'Nascimento',        value: fmtDate(alunoSelecionado.dataNascimento), icon: Calendar      },
                    { label: 'CPF',               value: alunoSelecionado.cpf,                     icon: Hash          },
                    { label: 'CIN',               value: alunoSelecionado.cin,                     icon: Hash          },
                    { label: 'Data de Matrícula', value: fmtDate(alunoSelecionado.dataCadastro),   icon: Calendar      },
                    { label: 'Escola Filiada',    value: alunoSelecionado.Afiliado,                icon: Building2     },
                  ].map(f => {
                    const Icon = f.icon;
                    return (
                      <div key={f.label} className="bg-cosmos-50/60 rounded-xl p-3 border border-cosmos-100/70">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-3.5 h-3.5 text-gold-500" />
                          <p className="text-xs text-cosmos-400 font-medium">{f.label}</p>
                        </div>
                        <p className="font-medium text-cosmos-900 text-sm">{f.value || '—'}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Endereço */}
              <div>
                <p className={`${text.label} mb-3`}>Endereço</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Logradouro', value: alunoSelecionado.endereco },
                    { label: 'CEP',        value: alunoSelecionado.cep      },
                    { label: 'Cidade',     value: alunoSelecionado.cidade   },
                    { label: 'Estado',     value: alunoSelecionado.estado   },
                  ].map(f => (
                    <div key={f.label} className="bg-cosmos-50/60 rounded-xl p-3 border border-cosmos-100/70">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-3.5 h-3.5 text-gold-500" />
                        <p className="text-xs text-cosmos-400 font-medium">{f.label}</p>
                      </div>
                      <p className="font-medium text-cosmos-900 text-sm">{f.value || '—'}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Curso */}
              <div>
                <p className={`${text.label} mb-3`}>Curso</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Nome do Curso',   value: alunoSelecionado.nomeCurso,                                               icon: GraduationCap },
                    { label: 'Carga Horária',   value: alunoSelecionado.cargaHoraria ? `${alunoSelecionado.cargaHoraria}h` : '—', icon: Timer         },
                    { label: 'Data de Início',  value: fmtDate(alunoSelecionado.dataInicioCurso),                                icon: Calendar      },
                    { label: 'Data de Término', value: fmtDate(alunoSelecionado.dataTerminoCurso),                               icon: Calendar      },
                  ].map(f => {
                    const Icon = f.icon;
                    return (
                      <div key={f.label} className="bg-cosmos-50/60 rounded-xl p-3 border border-cosmos-100/70">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-3.5 h-3.5 text-gold-500" />
                          <p className="text-xs text-cosmos-400 font-medium">{f.label}</p>
                        </div>
                        <p className="font-medium text-cosmos-900 text-sm">{f.value || '—'}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Métricas */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Cursos Ativos', value: alunoSelecionado.cursosAtivos,    icon: BookOpen    },
                  { label: 'Completos',     value: alunoSelecionado.cursosCompletos, icon: CheckCircle },
                  { label: 'Certificados',  value: alunoSelecionado.certificados,    icon: Award       },
                ].map(c => {
                  const Icon = c.icon;
                  return (
                    <div key={c.label} className="text-center p-4 rounded-xl border border-cosmos-100/70 bg-cosmos-50/60">
                      <Icon className="w-6 h-6 text-gold-500 mx-auto mb-2" strokeWidth={1.75} />
                      <p className="text-xl font-semibold text-cosmos-900">{c.value}</p>
                      <p className="text-xs text-cosmos-500">{c.label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Alerta risco alto */}
              {alunoSelecionado.risco === 'alto' && (
                <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0" strokeWidth={1.75} />
                    <div>
                      <p className="font-medium text-rose-800 text-sm">Aluno em Risco de Evasão</p>
                      <p className="text-xs text-rose-600 mt-0.5">
                        Recomenda-se contato imediato para identificar problemas e oferecer suporte.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer modal detalhes */}
            <div className="px-6 py-4 border-t border-cosmos-100 flex items-center gap-2">
              <button onClick={() => setModalAberto(false)}
                className="px-3 py-2 rounded-lg border border-cosmos-200 text-cosmos-600 text-sm font-medium hover:bg-cosmos-50 transition-colors">
                Fechar
              </button>

              <button onClick={() => handleDeletar(alunoSelecionado.id, alunoSelecionado.nome)} disabled={salvando}
                className="p-2 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors disabled:opacity-60"
                title="Excluir aluno">
                <Trash2 className="w-4 h-4" />
              </button>

              <div className="ml-auto flex items-center gap-2">
                {alunoSelecionado.risco === 'alto' && (
                  <button onClick={() => handleAcionarRisco(alunoSelecionado)} disabled={salvando}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors disabled:opacity-60">
                    {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                    Notificação
                  </button>
                )}

                <button
                  onClick={() => handleAtualizarStatus(
                    alunoSelecionado.id,
                    alunoSelecionado.statusMatricula === 'ativo' ? 'inactive' : 'active'
                  )}
                  disabled={salvando}
                  className="px-3.5 py-2 rounded-lg bg-cosmos-900 text-white text-sm font-medium hover:bg-cosmos-800 transition-colors disabled:opacity-60">
                  {alunoSelecionado.statusMatricula === 'ativo' ? 'Desativar' : 'Ativar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CADASTRO ───────────────────────────────────────────────────── */}
      {modalCadastro && (
        <ModalCadastroAluno
          onFechar={() => setModalCadastro(false)}
          onSucesso={() => { setModalCadastro(false); fetchAlunos(); fetchStats(); }}
        />
      )}
    </div>
  );
};

export default GerenciamentoAlunos;
